import type { Proposta, PropostaItem } from '@/types'

function safeParseJson(raw: unknown): Record<string, unknown> {
  if (!raw) return {}
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as unknown as Record<string, unknown>
  if (typeof raw !== 'string') return {}
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? { items: parsed } : (parsed as unknown as Record<string, unknown>)
  } catch {
    return {}
  }
}

/** Unifica itens da proposta (API, campo items ou dados.itens legado). */
export function resolvePropostaItems(proposta: Proposta): PropostaItem[] {
  const fromFields = proposta.itens?.length
    ? proposta.itens
    : proposta.items?.length
      ? proposta.items
      : []

  if (fromFields.length > 0) return fromFields

  const parsed = safeParseJson((proposta as Proposta & { dados?: string }).dados)
  const fromDados = parsed.items ?? parsed.itens
  if (!Array.isArray(fromDados) || fromDados.length === 0) return []

  return fromDados.map((item: Record<string, unknown>, index: number) => {
    const quantidade = Number(item.quantidade ?? 1)
    const valorUnitario = Number(
      item.valorUnitario ??
        (typeof item.valor === 'number' && item.valor > 10000 ? item.valor / 100 : item.valor) ??
        0,
    )
    const total = Number(item.total ?? valorUnitario * quantidade)
    return {
      ...item,
      id: String(item.id ?? `item-${index + 1}`),
      descricao: String(item.descricao ?? item.nome ?? item.name ?? `Item ${index + 1}`),
      nome: String(item.nome ?? item.descricao ?? item.name ?? `Item ${index + 1}`),
      tipo: (item.tipo as PropostaItem['tipo']) ?? 'plano',
      quantidade,
      valorUnitario,
      total,
    } as PropostaItem
  })
}

export function resolvePropostaTotalFinal(proposta: Proposta, items: PropostaItem[]): number {
  const parsed = safeParseJson((proposta as Proposta & { dados?: string }).dados)
  const fromField = Number(proposta.totalFinal ?? proposta.valor ?? 0)
  if (fromField > 0) return fromField
  const fromDados = Number(parsed.totalFinal ?? parsed.valor ?? 0)
  if (fromDados > 0) return fromDados
  return items.reduce((sum, item) => sum + Number(item.total ?? 0), 0)
}

/** Garante itens e total coerentes antes de exportar PDF/PPTX. */
export function normalizePropostaForExport(proposta: Proposta): Proposta {
  const items = resolvePropostaItems(proposta)
  const totalFinal = resolvePropostaTotalFinal(proposta, items)
  return {
    ...proposta,
    itens: items,
    items,
    totalFinal,
    valor: totalFinal,
  }
}
